<template>
  <Dialog
    v-model:visible="showModalRef"
    :close-on-escape="store.getters.dialogsCloseOnEscape"
    :dismissableMask="store.getters.dialogsDismissableMask"
    :modal="store.getters.dialogsModal"
    class="report-has-users-dialog"
    @hide="handleDialogHide"
  >
    <template #header>
      {{ getModalTitle }}
      <i
        class="custom-close-dialog-button cursor-pointer p-2 pi pi-times"
        @click="closeModal()"
      ></i>
    </template>
    <div>
      <div class="mt-1" v-if="showCloneChecks">
        <div>
          <Checkbox
            v-model="cloneUsers"
            :binary="true"
          />
          <label class="ml-1">Clonar usuarios</label>
        </div>
        <div class="mt-2">
          <Checkbox
            v-model="cloneGroups"
            :binary="true"
          />
          <label class="ml-1">Clonar grupos</label>
        </div>
      </div>
      
      <AutoComplete
        ref="autoCompleteRef"
        v-model="inputText"
        :suggestions="findReports"
        :placeholder="'Buscar informe de origen'"
        class="w-full autocomplete mt-3"
        @complete="search"
        @clear="handleClear"
        @item-select="addReport"
        :delay="600"
        optionLabel="alias"
      >
        <template #option="slotProps">
          <div>
            <b>{{ slotProps.option.alias }}</b> 
            <span class="ml-1" v-if="slotProps.option.titulo">({{ slotProps.option.titulo }})</span>
          </div>
        </template>
      </AutoComplete>
    </div>
    <template #footer>
      <div class="col-12">
        <div class="flex justify-content-between flex-wrap gap-3">
          <Button label="Cancelar" severity="secondary" @click="closeModal()" />
          <Button
            :label="'Importar'"
            @click="save"
            :disabled="
              !selectedReport || isSaving || (showCloneChecks && !cloneUsers && !cloneGroups)"
            :loading="isSaving"
            loading-icon="pi pi-spinner"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { computed, ref } from "vue";
import Dialog from "primevue/dialog";
import { useStore } from "vuex";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import AutoComplete from "primevue/autocomplete";
import {copyUsersAndGroups, searchReportsByText} from "@/api/reports/reports.api.js"; // eslint-disable-line
import { useToast } from "primevue/usetoast";
import { tipoClonacion } from "@/composables/_commons/enums.js";

// #region Props, Emits
// #endregion

// #region Params, Ref, Reactives, Computed
const emits = defineEmits(['operationOk']);
const store = useStore();
const loading = ref(false);
const isSaving = ref(false);
const aliasInforme = ref(null);
const showModalRef = ref(false);
const toast = useToast();
const inputText = ref("");
const selectedReport = ref(null);
const findReports = ref([]);
const clonedType = ref(tipoClonacion.Both);
const cloneUsers = ref(true);
const cloneGroups = ref(true);
const showCloneChecks = ref(false);

// #endregion

// #region Functions
// Open the modal with the data
function showModal(alias, type, showChecks) {
  aliasInforme.value = alias;
  clonedType.value = type;
  showCloneChecks.value = showChecks;

  // Show the modal
  showModalRef.value = true;
}

function closeModal() {
  showModalRef.value = false;
}

function handleDialogHide() {
  // Limpiar todos los datos cuando el dialog se cierra (por cualquier método)
  aliasInforme.value = null;
  inputText.value = "";
  selectedReport.value = null;
  cloneUsers.value = true;
  cloneGroups.value = true;
  showCloneChecks.value = false;
  findReports.value = [];
}

async function save() {
  isSaving.value = true;

  const data = { 
    aliasOrigen: selectedReport.value,
    aliasDestino: aliasInforme.value,
    tipoClonacion: getTipoClonacion.value
  };

  let response = await copyUsersAndGroups(data);

  if (response.data == true || response.data == "") {
    toast.add({
      severity: "success",
      summary: `Permisos copiados`,
      detail: getSuccessMessage.value,
      life: 3000,
    });
    closeModal();
    emits('operationOk');
  } else {
    const error = response.response?.data?.errors?.[0]?.message;

    if (!error) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: `No se ha podido realizar la copia.`,
        life: 3000,
      });
    } else {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error,
        life: 3000,
      });
    }
  }

  isSaving.value = false;
}


async function search(event) {
  const query = event.query;
  loading.value = true;

  const response = await searchReportsByText(query);
  findReports.value = response.data || [];
  loading.value = false;
}

const getTipoClonacion = computed(() => {
  if (showCloneChecks.value) {
    if (cloneUsers.value && cloneGroups.value) {
      return tipoClonacion.Both;
    } else if (cloneUsers.value) {
      return tipoClonacion.Users;
    } else if (cloneGroups.value) {
      return tipoClonacion.Groups;
    } else {
      return tipoClonacion.Both;
    }
  } else {
    return clonedType.value;
  }
});

const successDescriptions = {
  [tipoClonacion.Users]: 'Usuarios copiados al informe correctamente',
  [tipoClonacion.Groups]: 'Grupos copiados al informe correctamente',
  [tipoClonacion.Both]: 'Usuarios y grupos copiados al informe correctamente'
};

const getSuccessMessage = computed(() => {
  return successDescriptions[getTipoClonacion.value] || successDescriptions[tipoClonacion.Both];
});

const getModalTitle = computed(() => {
  if (clonedType.value === tipoClonacion.Users) {
    return "Importar usuarios";
  } else if (clonedType.value === tipoClonacion.Groups) {
    return "Importar grupos";
  } else {
    return "Importar usuarios y grupos";
  }
});

function handleClear() {
  inputText.value = "";
  selectedReport.value = null;
  search({ query: "" });
}

function addReport(event) {
  selectedReport.value = event.value.alias;
  inputText.value = `${event.value.alias}`;
}
// #endregion

// #region Effects, Hooks
// #endregion

// #region Provide, Expose
defineExpose({
  showModal,
});
// #endregion
</script>

<style lang="scss" scoped>
@import "@/styles/_vars.scss";

.autocomplete {
  :deep(.p-autocomplete-input) {
    width: 100%;
  }
}

.report-has-users-dialog {
  :deep(.p-dialog) {
    overflow: visible !important;
  }
  
  :deep(.p-dialog-content) {
    overflow: visible !important;
  }
  
  :deep(.p-autocomplete-panel) {
    position: relative !important;
    z-index: 9999 !important;
  }
}

.select-group-type {
  width: 12rem;
  padding: 0;

  .select-group-type-dropdown {
    border: 0;
    border-radius: 0;

    &.p-focus {
      box-shadow: none;
    }
  }
}

@media (min-width: $mobile-min-width) and (max-width: $mobile-max-width) {
  .autocomplete {
    max-width: unset;

    :deep(.p-autocomplete-input) {
      border-radius: 0;
    }
  }

  .select-group-type {
    min-width: 8rem;
  }
}
</style>